import * as Yup from 'yup';
import { isValid } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { Draggable, Droppable, DragDropContext } from '@hello-pangea/dnd';

import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/system';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Card,
  Grid,
  Button,
  Divider,
  Checkbox,
  useTheme,
  CardHeader,
  IconButton,
  Typography,
  CardContent,
  FormHelperText,
  FormControlLabel
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { OrganizationSetting } from 'src/services/organisation/organisation.types';
import {
  useGetOrganizationSettingsQuery,
  useGetReferenceTemplateSettingsQuery,
  useUpdateOrganizationSettingsMutation,
  useUpdateReferenceTemplateSettingsMutation
} from 'src/services/organisation/organisation.service';

import Iconify from 'src/components/iconify';
import { RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import FeatureLockedButton from 'src/components/billing/feature-locked-button';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';

import { FeatureType } from 'src/types/subscription.types';

// Define interface for question items
interface QuestionItem {
  question: string;
  required: boolean;
  order: number;
  id?: string;
}



export default function ReferenceSettingsCard() {

  const { t } = useTranslate();
  const theme = useTheme();

  const { organization, account } = useAuthContext();

  const { canAccess } = useFeatures();

  const { currentData: currentQuestions, isLoading: isLoadingQuestions } = useGetReferenceTemplateSettingsQuery(organization.id);

  const [updateTemplateQuestions, { isLoading: isSubmitting }] = useUpdateReferenceTemplateSettingsMutation();

  const [updateSettings] = useUpdateOrganizationSettingsMutation();

  const { currentData: settingsData, isLoading: isLoadingSettings } = useGetOrganizationSettingsQuery(
    organization.id
  );

  const [referenceTemplatesEditable, setReferenceTemplatesEditable] = useState(false);

  useEffect(() => {
    if (settingsData) {

      const referenceQuestionSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.REFERENCE_TEMPLATE_QUESTIONS_EDITABLE
      );

      if (referenceQuestionSetting) {
        setReferenceTemplatesEditable(referenceQuestionSetting.setting_value as boolean);
      }

    }
  }, [settingsData, setReferenceTemplatesEditable]);

  const onReferenceSettingsUpdate = async (value: boolean) => {

    setReferenceTemplatesEditable(value);

    try {
      const settingsToUpdate = [];

      if (!settingsData) return;

      const referenceQuestionSetting = settingsData.find(
        (setting) => setting.setting_type === OrganizationSetting.REFERENCE_TEMPLATE_QUESTIONS_EDITABLE
      );

      if (referenceQuestionSetting) {
        settingsToUpdate.push({
          id: referenceQuestionSetting.id,
          value
        });
      }

      if (settingsToUpdate.length === 0) return;

      await updateSettings({
        organizationId: organization.id,
        settings: settingsToUpdate,
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.business_settings.update.success'), { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(t('admin-organization.settings.business_settings.update.default_error'), { variant: 'error' });
    }
  }

  const TemplateQuestionsSchema = Yup.object().shape({
    questions: Yup.array()
      .of(
        Yup.object().shape({
          question: Yup.string().required(t('validation.required')),
          required: Yup.boolean().required(),
          order: Yup.number().required(t('validation.required')),
          id: Yup.string().optional(),
        })
      )
      .required(t('validation.required'))
      .min(1, t('validation.array_min', { length: 1 }))
      .max(15, t('validation.array_max', { length: 15 })),
  });

  // Ensure all questions have IDs for stable dragging
  const initialQuestions: QuestionItem[] = useMemo(() => {
    if (Array.isArray(currentQuestions) && currentQuestions.length > 0) {
      return currentQuestions.map((item: any) => ({
        ...item,
        id: item.id || `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }));
    }
    return [];
  }, [currentQuestions]);

  const defaultValues = {
    questions: initialQuestions
  };

  const methods = useForm<{ questions: QuestionItem[] }>({
    resolver: yupResolver(TemplateQuestionsSchema),
    defaultValues,
    mode: 'all',
  });

  const {
    reset,
    control,
    trigger,
    handleSubmit,
    getValues,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (Array.isArray(currentQuestions) && currentQuestions.length > 0) {
      // Ensure all questions have IDs for stable dragging
      const updatedQuestions = currentQuestions.map((item: any) => ({
        ...item,
        id: item.id || `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      }));

      reset({
        questions: updatedQuestions
      });
    }
  }, [currentQuestions, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'questions',
  });

  // Handle drag end event for reordering questions
  const onDragEnd = (result: any) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // check if the order has actually changed
    if (sourceIndex === destinationIndex) {
      return;
    }

    // fet the current form values to preserve all field data
    const currentValues = getValues();
    const questions = [...currentValues.questions];

    // Reorder the questions
    const [movedQuestion] = questions.splice(sourceIndex, 1);
    questions.splice(destinationIndex, 0, movedQuestion);

    // Update the order property for each question
    const updatedQuestions = questions.map((question, index) => ({
      ...question,
      order: index + 1
    }));

    // Update the form state with the reordered questions
    reset({
      questions: updatedQuestions
    });
  };

  const canEditSettings = useMemo(() => {
    if (!account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(account.org_role);
  }, [account]);

  const referenceTemplatesFeatureEnabled = useMemo(() => canAccess(FeatureType.REFERENCE_TEMPLATES_ENABLED), [canAccess]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {

      await updateTemplateQuestions({
        organizationId: organization.id,
        questions: data.questions
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.references.api.update.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('admin-organization.settings.references.api.update.default_error'), { variant: 'error' });
    }
  });

  if (isLoadingQuestions) {
    return (
      <Box sx={{ width: '100%' }}>
        <Card>
          <CardContent>
            <Typography>{t('common.loading')}</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="mdi:frequently-asked-questions"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" lineHeight={1.75}>
              {t('admin-organization.settings.references.title')}
            </Typography>
          }
        />

        <CardContent>
          <FormProvider {...methods}>
            <Stack direction="column" justifyContent="center" sx={{ mt: 2 }} gap={2}>

              <FormControlLabel
                disabled={!canEditSettings}
                label={
                  <Stack gap={0} sx={{ ml: 1 }}>
                    <Typography variant="body2">
                      {t('admin-organization.settings.references.labels.reference_templates_editable')}
                    </Typography>
                    <FormHelperText sx={{ mt: 0 }}>
                      {t('admin-organization.settings.references.labels.reference_templates_editable_helper')}
                    </FormHelperText>
                  </Stack>
                }
                control={<Checkbox checked={referenceTemplatesEditable} onChange={(e) => onReferenceSettingsUpdate(e.target.checked)} />}
              />

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="textSecondary">
                {t('admin-organization.settings.references.description')}
              </Typography>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="questions">
                  {(provided) => (
                    <Stack
                      spacing={2}
                      direction="column"
                      sx={{ alignItems: 'center' }}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {fields.map((field, index) => (
                        <Draggable
                          key={field.id || `question-${index}`}
                          draggableId={field.id || `question-${index}`}
                          index={index}
                          isDragDisabled={!canEditSettings || !referenceTemplatesFeatureEnabled}
                        >
                          {(draggable, snapshot) => (
                            <Grid
                              container
                              spacing={2}
                              sx={{ alignItems: 'center', px: 2, py: 1 }}
                              key={`${index}_${fields.length}`}
                              ref={draggable.innerRef}
                              {...draggable.draggableProps}
                              style={{
                                ...draggable.draggableProps.style
                              }}
                            >
                              <Grid
                                item
                                xs={11}
                                sm={8}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box
                                    {...draggable.dragHandleProps}
                                    sx={{
                                      mr: 1,
                                      color: 'text.disabled',
                                      cursor: canEditSettings && referenceTemplatesFeatureEnabled ? 'grab' : 'default'
                                    }}
                                  >
                                    <DragIndicatorIcon />
                                  </Box>
                                  <RHFTextField
                                    name={`questions.${index}.question`}
                                    label={t('admin-organization.settings.references.labels.question')}
                                    disabled={!canEditSettings || !referenceTemplatesFeatureEnabled}
                                    fullWidth
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={9} sm={2}>
                                <RHFCheckbox
                                  name={`questions.${index}.required`}
                                  label={t('admin-organization.settings.references.labels.required')}
                                  disabled={!canEditSettings || !referenceTemplatesFeatureEnabled}
                                />
                              </Grid>

                              <Grid item xs={3} sm={2}>
                                <IconButton sx={{ mt: .5 }} onClick={() => remove(index)} color="error" disabled={!canEditSettings || !referenceTemplatesFeatureEnabled}>
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </Stack>
                  )}
                </Droppable>
              </DragDropContext>

              {
                errors.questions?.root && (
                  <Typography variant="caption" color="error">
                    {errors.questions?.root.message}
                  </Typography>
                )
              }

              <Box sx={{ display: 'flex', justifyContent: 'start' }}>
                <Button
                  startIcon={<Iconify icon="gridicons:add-outline" />}
                  variant="contained" color="primary"
                  onClick={() => append({
                    question: '',
                    required: false,
                    order: fields.length + 1,
                    id: `question-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
                  })}
                  disabled={!canEditSettings || !referenceTemplatesFeatureEnabled || fields.length >= 15}
                >
                  {t('admin-organization.settings.references.labels.add_question')}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>

                {
                  referenceTemplatesFeatureEnabled ? (
                    <LoadingButton
                      type="submit"
                      onClick={onSubmit}
                      variant="contained"
                      loading={isSubmitting}
                      disabled={!isValid || !canEditSettings}
                    >
                      {t('common.update')}
                    </LoadingButton>
                  ) : (
                    <FeatureLockedButton
                      label={t(`common.subscription_required`)}
                      variant="contained"
                    />
                  )
                }
              </Box>
            </Stack>
          </FormProvider>

        </CardContent>
      </Card>
    </Box>
  )
}